/* eslint-disable sonarjs/cognitive-complexity, react/forbid-component-props */
import { faLock, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { Formik } from 'formik';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import Cookies from 'react-cookies';
import { useSelector, useDispatch } from 'react-redux';
import { object, string } from 'yup';
import { COOKIES_CONFIG } from '../../brands/default/config';
import { COOKIES, PAGE_TYPES } from '../../brands/default/constants';
import { LOGIN_FORM } from '../../brands/default/content/components/login';
import {
  FACEBOOK_LOGIN_ENABLED,
  GOOGLE_ENABLED,
  TEXT_LOWERCASE,
} from '../../brands/default/features';
import { LINK_FORGOT_PASSWORD, LINK_HOME } from '../../brands/default/links';
import { trackEvent } from '../../helpers/analytics';
import { setCookie } from '../../helpers/cookieSetter';
import ErrorTrackingService from '../../helpers/errorTrackingService';
import { unsubscribeAllUser } from '../../helpers/unsubscribe';
import { getUserStatus } from '../../helpers/userStatus';
import { setUserEmail } from '../../redux/actions/email';
import { setLoadingState } from '../../redux/actions/loading';
import { logUser, loginSuccess } from '../../redux/actions/user';
import { LOADING_STATUS } from '../../redux/reducers/loading';
import Message from '../_generic/Message';
import { sendBotAlert } from '../_generic/axiosSplunk/axiosSplunk';
import ButtonForm from '../_generic/button/CTAButton';
import FormikInput from '../_generic/input/FormikInput';
import TextDevider from '../_generic/textDevider/TextDevider';
import useToast from '../_generic/toast/UseToast';
import { panel } from '../checkout/details/UserPanelSetup';
import DeleteAccountMessage from './DeleteAccountMessage';
import { DisableAutofill } from './DisableAutofill';
import ForgotPassword from './ForgotPassword';
import LoginMediaButtons from './LoginMediaButtons';
import PrivacyText from './PrivacyText';

const schema = object().shape({
  email: string()
    .required(LOGIN_FORM.requiredField)
    .email(LOGIN_FORM.enterValidEmail),
  password: string()
    .required(LOGIN_FORM.requiredField)
    .min(6, LOGIN_FORM.passwordSixCharacters),
});

const LoginForm = ({
  isLoginPage = false,
  setActivePanel,
  enableAutofill = false,
  vipPaywall,
}) => {
  const isDevelopment = process.env.NEXT_PUBLIC_ENVIRONMENT === 'dev';
  const toast = useToast();
  const dispatch = useDispatch();
  const [userEmail] = useSelector((state) => [state.email]);
  const [botField, setBotField] = useState('');
  const router = useRouter();
  const [isLocked, setIsLocked] = useState(false);
  const [isFBAccount, setIsFBAccount] = useState(false);
  const [emailOpt, setEmailOpt] = useState(false);

  const {
    invalidCredentials,
    anErrorOccured,
    formEmail,
    formPassword,
    loginPrivacyPolicy,
    loginPrivacyPolicyCheckout,
    loginText,
    registerPrivacyPolicy,
    signInWithFacebookOnly,
    lockedAccount,
    userDoesntExists,
  } = LOGIN_FORM;

  const actionURL = router.asPath.includes('/login')
    ? '/login'
    : `/login?redirect=${router.asPath}`;
  const failedLogin = router.asPath.includes('login-failed');

  const isDeleteAccount = Cookies.load(COOKIES.deleteEmail);

  const showErrorToast = (message) => toast.addToast(message, 'toast-error');

  const handleHPField = (event) => {
    setBotField(event.target.value);
  };

  const isCheckout = router.asPath.includes(PAGE_TYPES.CHECKOUT);

  useEffect(() => {
    setCookie({ key: COOKIES.emailOptOut, value: false });
    if (failedLogin) {
      showErrorToast(invalidCredentials);
    }
  }, []);

  useEffect(() => {
    const emailToggle = document.querySelector('#emailToggle');
    const handler = () => {
      setEmailOpt(!emailOpt);
      setCookie({ key: COOKIES.emailOptOut, value: !emailOpt });
    };
    if (emailToggle) {
      emailToggle.addEventListener('click', handler);

      return () => {
        emailToggle.removeEventListener('click', handler);
      };
    }

    return null;
  });

  const sendForm = async (values) => {
    if (
      botField.length > 0 ||
      document.querySelector('#alt_email').value.length > 0
    ) {
      router.push(LINK_HOME);
      sendBotAlert(router);

      return;
    }

    dispatch(setLoadingState(LOADING_STATUS.LOADING));

    try {
      const response = await logUser(values.email, values.password);
      setIsLocked(response?.fieldErrors?.isUserAccountLocked);
      if (response.data?.customerToken) {
        dispatch(loginSuccess(response.data));
        trackEvent('log_in');
        if (vipPaywall) {
          trackEvent('viphub: paywall login');
        }
        setCookie(
          COOKIES.brandCookie,
          'registered_user',
          COOKIES_CONFIG.COOKIE_SUBSCRIBED_EXPIRES,
        );

        setCookie(COOKIES.userLoggedIn, true);
        setCookie(
          COOKIES.registered,
          true,
          COOKIES_CONFIG.COOKIE_SUBSCRIBED_EXPIRES,
        );

        // if emailOpt is true we need to call unsubscribe from all emails endpoint
        if (emailOpt) {
          await unsubscribeAllUser(response.data?.customerToken);
        }
      } else {
        showErrorToast(response.message);
      }
    } catch (error) {
      ErrorTrackingService.logError(error);
      showErrorToast(error.message || anErrorOccured);
    }
    dispatch(setLoadingState(LOADING_STATUS.READY));
  };

  // Work around: This block can be removed when Google Chrome fix the
  // autofill="off" bug. Check DisableAutofill for details.
  const [isInputDisable, SetIsInputDisable] = useState(!enableAutofill);

  useEffect(() => {
    const interval = setInterval(() => {
      SetIsInputDisable(false);
      clearInterval(interval);
    }, 1_000);

    // Specify how to clean up after this effect:
    return function cleanup() {
      clearInterval(interval);
    };
  }, []);
  // End of the work around

  const setUserDetails = async (email) => {
    try {
      const response = await getUserStatus(email);
      if (response) {
        setIsFBAccount(response.status.isRegistered && response.status.FBO);
        if (response.isLocked) {
          setIsLocked(true);

          return;
        }
        if (!response.isNewUser) return;
        if (response.userEmail) dispatch(setUserEmail(response.userEmail));
        if (setActivePanel) setActivePanel(panel.registration);
        toast.addToast(userDoesntExists, 'toast-info');
      }
    } catch (error) {
      ErrorTrackingService.logError(error);
      showErrorToast(error.message || anErrorOccured);
    }
  };

  const extraClasses = TEXT_LOWERCASE ? 'lowercase_placeholder' : '';

  return (
    <>
      {(GOOGLE_ENABLED || FACEBOOK_LOGIN_ENABLED) && (
        <LoginMediaButtons emailOptOut={emailOpt} isLoginPage={isLoginPage} />
      )}
      {(GOOGLE_ENABLED || FACEBOOK_LOGIN_ENABLED) && (
        <TextDevider vipPaywall={vipPaywall} />
      )}
      {isLocked && (
        <Message
          data-testid="locked-account"
          html
          iconPosition="left"
          message={lockedAccount(LINK_FORGOT_PASSWORD)}
          type="ERROR"
        />
      )}
      {isDeleteAccount && <DeleteAccountMessage email={isDeleteAccount} />}

      <Formik
        enableReinitialize
        initialValues={{
          email: userEmail.userEmail || '',
          password: '',
        }}
        onSubmit={async (values) => sendForm(values)}
        validateOnBlur
        validateOnChange
        validateOnMount
        validationSchema={schema}
      >
        {({
          handleChange,
          handleSubmit,
          touched,
          handleBlur,
          isSubmitting,
          isValid,
          values,
          errors,
        }) => {
          const blurFunction = (event) => {
            const value = event.target.value;
            const name = event.target.name;
            handleBlur(event);
            if (!value) return;
            if (name === 'password') trackEvent('login_password_field');
            if (name === 'email') trackEvent('login_email_field');
            if (name === 'email' && !errors.email) setUserDetails(value);
          };

          return (
            <form
              action={actionURL}
              method="POST"
              name="login-form"
              onSubmit={isDevelopment ? handleSubmit : null}
            >
              {!enableAutofill && <DisableAutofill />}
              <FormikInput
                dataqa="enterEmail"
                disable={isInputDisable}
                errorText={errors.email}
                extraClasses={extraClasses}
                icon={faEnvelope}
                inputError={errors.email}
                inputTouched={touched.email}
                inputValue={values.email}
                name={'email'}
                onBlurFunction={(event) => blurFunction(event)}
                onChangeFunction={handleChange}
                placeholder={formEmail}
                type="email"
              />
              <FormikInput
                dataqa="enterPassword"
                errorText={errors.password}
                extraClasses={extraClasses}
                icon={faLock}
                inputError={errors.password}
                inputTouched={touched.password}
                inputValue={values.password}
                name={'password'}
                onBlurFunction={(event) => blurFunction(event)}
                onChangeFunction={handleChange}
                placeholder={formPassword}
                type="password"
              />

              <input
                aria-label="alt email"
                autoComplete="off"
                className="hp-container"
                id="alt_email"
                name="alt_email"
                onChange={handleHPField}
                tabIndex="-1"
                type="text"
              />

              <ButtonForm
                className="full-width button"
                dataqa="login"
                disabled={isSubmitting || !isValid}
                type="submit"
              >
                {loginText}
              </ButtonForm>
              {isCheckout ? (
                <PrivacyText
                  text={loginPrivacyPolicyCheckout(
                    emailOpt ? "- I'M OUT!" : '',
                  )}
                />
              ) : (
                <PrivacyText
                  text={vipPaywall ? registerPrivacyPolicy : loginPrivacyPolicy}
                />
              )}
              {isFBAccount && (
                <p className="facebook-login text-center">
                  <b>{signInWithFacebookOnly}</b>
                </p>
              )}
              <ForgotPassword email={values.email} isFBOnly={isFBAccount} />
            </form>
          );
        }}
      </Formik>

      <style jsx>{`
        .facebook-login {
          font-size: 15px;
        }
        .hp-container {
          opacity: 0;
          position: absolute;
          top: 0;
          left: 0;
          height: 0;
          width: 0;
          z-index: -1;
        }
      `}</style>
      <style global jsx>{`
        .form-control:disabled,
        .form-control[readonly] {
          background-color: #ffffff;
        }
        .email-trick input {
          color: #999999;
        }
      `}</style>
    </>
  );
};

LoginForm.propTypes = {
  enableAutofill: PropTypes.bool,
  isLoginPage: PropTypes.bool,
  setActivePanel: PropTypes.func,
  vipPaywall: PropTypes.bool,
};

export default LoginForm;
